import { useEffect, useRef, useState } from 'react';
import { useLocalStorageState } from '../local-storage.hooks';
import {
  RepairCostingFilterType,
  RepairCostingLocalStorage,
} from '@/entities/repair-costing';

interface RepairCostingFilterProps {
  setCurrentPage: (page: number) => void;
}

export const useRepairCostingFilter = ({
  setCurrentPage,
}: RepairCostingFilterProps) => {
  const [debouncedFilter, setDebouncedFilter] =
    useState<RepairCostingFilterType>({
      equipmentId: '',
      damageName: '',
      damageSeverity: '',
      repairServiceId: '',
      status: '',
    });
  const filtersEmptyRef = useRef<boolean>(true);

  const [equipmentId, setEquipmentId] = useLocalStorageState({
    key: RepairCostingLocalStorage.EQUIPMENT_ID,
    removeOnClose: true,
  });

  const [damageName, setDamageName] = useLocalStorageState({
    key: RepairCostingLocalStorage.DAMAGE_NAME,
    removeOnClose: true,
  });

  const [damageSeverity, setDamageSeverity] = useLocalStorageState({
    key: RepairCostingLocalStorage.DAMAGE_SEVERITY,
    removeOnClose: true,
  });

  const [repairServiceId, setRepairServiceId] = useLocalStorageState({
    key: RepairCostingLocalStorage.REPAIR_SERVICE_ID,
    removeOnClose: true,
  });

  const [status, setStatus] = useLocalStorageState({
    key: RepairCostingLocalStorage.STATUS,
    removeOnClose: true,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      equipmentId: equipmentId || '',
      damageName: damageName || '',
      damageSeverity: damageSeverity || '',
      repairServiceId: repairServiceId || '',
      status,
    }));

    filtersEmptyRef.current =
      !equipmentId &&
      !damageName &&
      !damageSeverity &&
      !repairServiceId &&
      !status;
  }, [equipmentId, damageName, damageSeverity, repairServiceId, status]);

  const searchByEquipmentId = (value: string) => {
    setEquipmentId(value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, equipmentId: value }));
  };

  const searchByDamageName = (value: string) => {
    setDamageName(value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, damageName: value }));
  };

  const searchByDamageSeverity = (value: string) => {
    setDamageSeverity(value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, damageSeverity: value }));
  };

  const searchByRepairServiceId = (value: string) => {
    setRepairServiceId(value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, repairServiceId: value }));
  };

  const searchByStatus = (value: string) => {
    setStatus(value);
    setCurrentPage(1);
    setDebouncedFilter((prev) => ({ ...prev, status: value }));
  };

  const clearFilters = () => {
    setEquipmentId('');
    setDamageName('');
    setDamageSeverity('');
    setRepairServiceId('');
    setStatus('');
    setCurrentPage(1);
  };

  return {
    debouncedFilter,
    filtersEmpty: filtersEmptyRef.current,
    clearFilters,
    equipmentId,
    damageName,
    damageSeverity,
    repairServiceId,
    status,
    searchByEquipmentId,
    searchByDamageName,
    searchByDamageSeverity,
    searchByRepairServiceId,
    searchByStatus,
  };
};
